<template>
  <div class="refund-list">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <el-icon size="24">
            <RefreshLeft />
          </el-icon>
        </div>
        <div class="title-content">
          <h2>退款管理</h2>
          <p>管理系统退款信息，包括退款查询、确认等操作</p>
        </div>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-section">
      <el-card class="filter-card">
        <el-form :model="filterForm" :inline="true" class="filter-form">
          <el-form-item label="选择用户">
            <el-select 
              v-model="filterForm.user_id" 
              placeholder="请选择用户" 
              clearable
              filterable
              size="large"
              class="filter-select"
              @change="handleUserChange"
            >
              <el-option 
                v-for="user in userOptions" 
                :key="user.user_id" 
                :label="user.name" 
                :value="user.user_id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="开始时间">
            <el-date-picker
              v-model="filterForm.start_time"
              type="datetime"
              placeholder="选择开始时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              size="large"
              class="filter-date-picker"
              clearable
            />
          </el-form-item>
          <el-form-item label="结束时间">
            <el-date-picker
              v-model="filterForm.end_time"
              type="datetime"
              placeholder="选择结束时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              size="large"
              class="filter-date-picker"
              clearable
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch" size="large">
              <el-icon><Search /></el-icon>
              查询
            </el-button>
            <el-button @click="handleReset" size="large">
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 统计信息 -->
    <div class="stats-section">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card class="stats-card total-pay">
            <div class="stats-content">
              <div class="stats-icon">
                <el-icon size="32"><Money /></el-icon>
              </div>
              <div class="stats-info">
                <div class="stats-label">总计支付金额</div>
                <div class="stats-value">¥{{ formatAmount(stats.total_pay_amount) }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="stats-card total-refund">
            <div class="stats-content">
              <div class="stats-icon">
                <el-icon size="32"><RefreshLeft /></el-icon>
              </div>
              <div class="stats-info">
                <div class="stats-label">总计退款金额</div>
                <div class="stats-value">¥{{ formatAmount(stats.total_refund_amount) }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 退款列表 -->
    <div class="table-section">
      <div class="table-header">
        <div class="table-title">
          <el-icon size="18">
            <List />
          </el-icon>
          <span>退款列表</span>
        </div>
        <div class="table-info">
          <span class="total-count">共 {{ pagination.total }} 条记录</span>
        </div>
      </div>
      
      <el-table 
        v-loading="loading" 
        :data="refundList" 
        border 
        stripe 
        class="refund-table"
        :header-cell-style="{ background: '#f8f9fa', color: '#606266' }"
        table-layout="auto"
        v-if="refundList.length > 0 || !loading"
      >
        <el-table-column prop="id" label="退款ID" width="80" align="center" />
        <el-table-column label="单号信息" align="center">
          <template #default="{ row }">
            <div class="refund-info-cell">
              <div class="order-sn">订单号：{{ row.order_sn }}</div>
              <div class="refund-sn">退款单号：{{ row.refund_sn }}</div>
              <div class="refund-no">流水号：{{ row.refund_no }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="订单金额" width="120" align="center">
          <template #default="{ row }">
            <span class="amount-text">¥{{ row.amount }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="refund_amount" label="退款金额" width="120" align="center">
          <template #default="{ row }">
            <span class="refund-amount-text">¥{{ row.refund_amount }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="user_id" label="用户" width="120" align="center">
          <template #default="{ row }">
            <span>{{ getUserName(row.user_id) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="is_pay" label="退款状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.is_pay === 1 ? 'success' : 'warning'" size="small">
              {{ row.is_pay === 1 ? '已退款' : '待退款' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="时间信息" width="260" align="center">
          <template #default="{ row }">
            <div class="time-info-cell">
              <div class="pay-time">支付时间：{{ formatTime(row.pay_time) }}</div>
              <div class="refund-time">退款时间：{{ formatTime(row.refund_time) }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="160" align="center">
          <template #default="{ row }">
            <span>{{ formatTime(row.created_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button type="success" v-if="row.is_pay === 2" size="small" class="confirm-btn" @click="handleConfirm(row)">
                <el-icon><Check /></el-icon>
                确认退款
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态显示 -->
      <div v-if="!loading && refundList.length === 0" class="empty-state">
        <el-empty description="暂无退款数据" />
      </div>

      <!-- 分页 -->
      <div class="pagination-section" v-if="refundList.length > 0">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { RefreshLeft, Search, Refresh, List, Check, Money } from '@element-plus/icons-vue'
import { getRefundList,getRefundConfirm } from '@/api/refund'
import { getUserList } from '@/api/user'

export default {
  name: 'RefundListPage',
  components: {
    RefreshLeft,
    Search,
    Refresh,
    List,
    Check,
    Money
  },
  setup() {
         // 响应式数据
     const loading = ref(false)
     const refundList = ref([])
     const userOptions = ref([])
     
     // 统计数据
     const stats = reactive({
       total_pay_amount: 0,
       total_refund_amount: 0
     })
    
    // 筛选表单
    const filterForm = reactive({
      user_id: '',
      start_time: '',
      end_time: ''
    })
    
    // 分页信息
    const pagination = reactive({
      current: 1,
      size: 10,
      total: 0
    })

    // 方法
    const loadUserOptions = async () => {
      try {
        const response = await getUserList({ page: 1, per_page: 1000 })
        if (response && response.list) {
          userOptions.value = response.list
        } else if (Array.isArray(response)) {
          userOptions.value = response
        } else {
          userOptions.value = response.data || response.records || []
        }
      } catch (error) {
        console.error('加载用户列表失败:', error)
        ElMessage.error('加载用户列表失败')
      }
    }

    const loadRefundList = async () => {
      loading.value = true
      try {
        // 构建查询参数
        const params = {
          page: pagination.current,
          per_page: pagination.size,
          ...filterForm
        }
        
        const response = await getRefundList(params)
        
                 // 处理响应数据
         if (response && response.list) {
           refundList.value = response.list
           pagination.total = response.total || response.list.length
           // 计算统计数据
           calculateStats(response.list)
         } else if (Array.isArray(response)) {
           refundList.value = response
           pagination.total = response.length
           // 计算统计数据
           calculateStats(response)
         } else {
           refundList.value = response.data || response.records || []
           pagination.total = response.total || response.totalCount || 0
           // 计算统计数据
           calculateStats(response.data || response.records || [])
         }
      } catch (error) {
        console.error('加载退款列表失败:', error)
        ElMessage.error('加载退款列表失败')
      } finally {
        loading.value = false
      }
    }

    const getUserName = (userId) => {
      const user = userOptions.value.find(u => u.user_id === userId)
      return user ? user.name : `用户${userId}`
    }

         const formatTime = (timeStr) => {
       if (!timeStr) return '-'
       try {
         const date = new Date(timeStr)
         return date.toLocaleString('zh-CN', {
           year: 'numeric',
           month: '2-digit',
           day: '2-digit',
           hour: '2-digit',
           minute: '2-digit',
           second: '2-digit'
         })
       } catch (error) {
         return timeStr
       }
     }

          const formatAmount = (amount) => {
       if (!amount && amount !== 0) return '0.00'
       return Number(amount).toFixed(2)
     }

     const calculateStats = (list) => {
       let totalPay = 0
       let totalRefund = 0
       
       list.forEach(item => {
         if (item.amount) {
           totalPay += Number(item.amount) || 0
         }
         if (item.refund_amount) {
           totalRefund += Number(item.refund_amount) || 0
         }
       })
       
       stats.total_pay_amount = totalPay
       stats.total_refund_amount = totalRefund
     }

     const handleUserChange = () => {
      // 用户选择变化时，可以在这里添加其他逻辑
    }

    const handleSearch = () => {
      // 验证时间范围
      if (filterForm.start_time && filterForm.end_time) {
        const startTime = new Date(filterForm.start_time)
        const endTime = new Date(filterForm.end_time)
        if (startTime > endTime) {
          ElMessage.warning('开始时间不能晚于结束时间')
          return
        }
      }
      
      pagination.current = 1
      loadRefundList()
    }

    const handleReset = () => {
      filterForm.user_id = ''
      filterForm.start_time = ''
      filterForm.end_time = ''
      pagination.current = 1
      loadRefundList()
    }

    const handleDetail = () => {
      // TODO: 详情弹窗功能稍后实现
      ElMessage.info('详情功能开发中...')
    }

    const handleConfirm = async (row) => {
      console.log(row)
      const res = await getRefundConfirm({id:row.id,user_id:row.user_id})
      console.log(res)
      if(res.result){
        ElMessage.success('确认退款成功')
        loadRefundList()
      }else{
        ElMessage.error('确认退款失败')
      }
     
    }

    const handleSizeChange = (size) => {
      pagination.size = size
      pagination.current = 1
      loadRefundList()
    }

    const handleCurrentChange = (current) => {
      pagination.current = current
      loadRefundList()
    }

    // 生命周期
    onMounted(() => {
      loadUserOptions()
      loadRefundList()
    })

         return {
       loading,
       refundList,
       userOptions,
       filterForm,
       pagination,
       stats,
       getUserName,
       formatTime,
       formatAmount,
       handleUserChange,
       handleSearch,
       handleReset,
       handleDetail,
       handleConfirm,
       handleSizeChange,
       handleCurrentChange
     }
  }
}
</script>

<style scoped>
.refund-list {
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
}

/* 页面标题区域 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
}

.page-title {
  display: flex;
  align-items: center;
}

.title-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  margin-right: 16px;
  backdrop-filter: blur(10px);
}

.title-content h2 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
}

.title-content p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

 /* 筛选区域 */
 .filter-section {
   margin-bottom: 24px;
 }

 /* 统计信息区域 */
 .stats-section {
   margin-bottom: 24px;
 }

 .stats-card {
   border-radius: 16px;
   box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
   transition: all 0.3s ease;
 }

 .stats-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
 }

 .stats-content {
   display: flex;
   align-items: center;
   padding: 20px;
 }

 .stats-icon {
   margin-right: 20px;
   padding: 16px;
   border-radius: 12px;
   display: flex;
   align-items: center;
   justify-content: center;
 }

 .total-pay .stats-icon {
   background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
   color: white;
 }

 .total-refund .stats-icon {
   background: linear-gradient(135deg, #f56c6c 0%, #f78989 100%);
   color: white;
 }

 .stats-info {
   flex: 1;
 }

 .stats-label {
   font-size: 14px;
   color: #909399;
   margin-bottom: 8px;
 }

 .stats-value {
   font-size: 28px;
   font-weight: 700;
   color: #303133;
 }

.filter-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.filter-form {
  padding: 20px;
}

.filter-select {
  min-width: 200px;
}

.filter-date-picker {
  min-width: 200px;
}

/* 表格区域 */
.table-section {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 100%;
}

.table-header {
  background: #f8f9fa;
  padding: 20px 24px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  display: flex;
  align-items: center;
  color: #606266;
  font-weight: 500;
}

.table-title .el-icon {
  margin-right: 8px;
  color: #409eff;
}

.total-count {
  color: #909399;
  font-size: 14px;
}

.refund-table {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}

.amount-text {
  color: #67c23a;
  font-weight: 600;
}

 .refund-amount-text {
   color: #f56c6c;
   font-weight: 600;
 }

   .refund-info-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
    padding: 10px 0;
    min-height: 70px;
    justify-content: center;
  }

  .order-sn {
    font-weight: 600;
    color: #303133;
    font-size: 13px;
    line-height: 1.4;
    background: #f0f9ff;
    padding: 2px 6px;
    border-radius: 4px;
    border-left: 3px solid #409eff;
  }

  .refund-sn {
    font-weight: 600;
    color: #409eff;
    font-size: 13px;
    line-height: 1.4;
  }

  .refund-no {
    color: #909399;
    font-size: 12px;
    word-break: break-all;
    line-height: 1.3;
    background: #f5f7fa;
    padding: 2px 6px;
    border-radius: 4px;
    border-left: 3px solid #e4e7ed;
  }

  .time-info-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
    padding: 10px 0;
    min-height: 50px;
    justify-content: center;
  }

  .pay-time {
    font-weight: 600;
    color: #67c23a;
    font-size: 13px;
    line-height: 1.4;
  }

  .refund-time {
    color: #f56c6c;
    font-size: 12px;
    line-height: 1.3;
    background: #fef0f0;
    padding: 2px 6px;
    border-radius: 4px;
    border-left: 3px solid #fbc4c4;
  }

 .action-buttons {
   display: flex;
   gap: 8px;
   justify-content: center;
   align-items: center;
 }

 .detail-btn,
 .confirm-btn {
   border-radius: 8px;
 }

/* 确保表格完全铺满空间 */
:deep(.el-table) {
  width: 100% !important;
}

:deep(.el-table__body-wrapper) {
  width: 100% !important;
}

:deep(.el-table__header-wrapper) {
  width: 100% !important;
}

/* 表格行和单元格铺满 */
:deep(.el-table__row) {
  width: 100% !important;
}

:deep(.el-table__cell) {
  width: auto !important;
}

/* 空状态样式 */
.empty-state {
  padding: 60px 24px;
  text-align: center;
  background: white;
}

/* 分页区域 */
.pagination-section {
  padding: 24px;
  background: #f8f9fa;
  text-align: right;
}

.pagination {
  justify-content: flex-end;
}

/* 响应式设计优化 */
@media (max-width: 1200px) {
  .refund-list {
    padding: 20px;
  }
  
  .page-header {
    padding: 24px;
  }
  
  .table-section {
    margin-bottom: 20px;
  }
}

@media (max-width: 768px) {
  .refund-list {
    padding: 16px;
  }
  
  .page-header {
    padding: 20px;
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  /* 移动端表格优化 */
  :deep(.el-table) {
    font-size: 12px;
  }
  
  :deep(.el-table .cell) {
    padding: 8px 4px;
  }
}
</style>
